import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Container,
  Backdrop,
  Chip,
  Divider,
  Card,
  CardContent,
  IconButton,
  Alert,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Avatar,
  Stack,
  AppBar,
  Toolbar,
} from "@mui/material";
import { styled, useTheme, alpha } from "@mui/material/styles";
import {
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarTodayIcon,
  Person as PersonIcon,
  AttachMoney as AttachMoneyIcon,
  HelpOutline as HelpOutlineIcon,
} from "@mui/icons-material";
import { useQuery, useMutation } from "react-query";
import client from "../../../API";
import { useCreator } from "../../../Hooks/agencySelectedCreatorContext";
import useAlertDialog from "../../../Components/useAlertDialog";
import AlertDialog from "../../../Components/AlertDialog";
import ChatScreen from "../../../Components/ChatScreen";
import { formatMessageContent } from "../../../Utils/constants";
import PageTitle from "../../../Components/PageTitle";
import CreatorViewOfferDialog from "../../../Components/ChatComponents/CreatorViewOfferDialog";

// Constants for mobile layout
const MOBILE_HEADER_HEIGHT = 56;

// Utility function to format the due date
function formatFinishDate(dateString) {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Invalid Date";
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear().toString().substr(-2);
  return `${month}/${day}/${year}`;
}

// Status tooltip messages
const getStatusTooltip = (status) => {
  const statusTooltips = {
    pending: "Partnership offer is pending - waiting for response",
    active: "Partnership is active and in progress",
    completed: "Partnership has been completed",
    declined: "Partnership offer was declined",
  };
  return statusTooltips[status] || `Current status: ${status}`;
};

// Styled Components (reusing campaign page styles)
const MainContainer = styled(Container)(({ theme }) => ({
  padding: 0,
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  maxWidth: "none !important",
  [theme.breakpoints.down("lg")]: {
    height: "calc(var(--vh, 1vh) * 100)",
    maxHeight: "calc(var(--vh, 1vh) * 100)",
    overflow: "hidden",
  },
  [theme.breakpoints.up("lg")]: {
    padding: theme.spacing(2),
    height: "calc(100vh - 32px)",
  },
}));

const DesktopChatContainer = styled(Box)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("lg")]: {
    display: "flex",
    height: "100%",
    gap: theme.spacing(2),
    overflow: "hidden",
  },
}));

const MobileChatContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflow: "hidden",
  position: "relative",
  [theme.breakpoints.up("lg")]: {
    display: "none",
  },
}));

const DesktopSidebar = styled(Card)(({ theme }) => ({
  minWidth: 380,
  maxWidth: 420,
  height: "100%",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
}));

const DesktopChatCard = styled(Card)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflow: "hidden",
  minWidth: 0,
}));

const MobileCampaignAccordion = styled(Accordion)(({ theme }) => ({
  margin: 0,
  boxShadow: "none",
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: 0,
  },
  "&.chat-mode-hidden": {
    display: "none",
  },
}));

const MobileScrollableContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  WebkitOverflowScrolling: "touch",
  position: "relative",
  "&::-webkit-scrollbar": {
    width: 4,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.text.primary, 0.2),
    borderRadius: 2,
  },
}));

const MobileFullscreenChat = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: theme.palette.background.default,
  zIndex: theme.zIndex.modal - 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
}));

const MobileChatHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  minHeight: MOBILE_HEADER_HEIGHT,
  flexShrink: 0,
}));

const InfoSection = styled(Box)(({ theme, compact = false }) => ({
  padding: compact ? theme.spacing(1.5) : theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-child": {
    borderBottom: "none",
  },
  "& + &": {
    borderTop: "none",
  },
}));

const ChatContentWrapper = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  minHeight: 0,
});

const PartnershipBriefCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(2, 2, 0, 2),
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.02
  )}, ${alpha(theme.palette.secondary.main, 0.02)})`,
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: theme.shadows[4],
  },
  [theme.breakpoints.up("lg")]: {
    margin: 0,
    marginBottom: theme.spacing(2),
  },
}));

const AgencyPartnershipPage = () => {
  const { partnershipId, creatorId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { creatorData } = useCreator();

  const [mobileActiveAccordion, setMobileActiveAccordion] = useState("details");
  const [mobileChatFullscreen, setMobileChatFullscreen] = useState(false);
  const [selectedDeliverable, setSelectedDeliverable] = useState(null);

  const { dialogState, openDialog: openAlert, closeDialog } = useAlertDialog();
  const chatRef = useRef(null);

  const username = creatorData?.creator?.creator || creatorId;
  const displayName = creatorData?.creator?.display_name || username;

  // Fetch partnership data
  const {
    data: partnershipData,
    isLoading: partnershipLoading,
    error: partnershipError,
    refetch: refetchPartnership,
  } = useQuery({
    queryKey: ["partnerships", partnershipId],
    queryFn: () => client.partnerships.fetchPartnership(partnershipId),
    enabled: !!partnershipId && !!creatorId,
    refetchOnWindowFocus: false,
  });

  const partnership = partnershipData?.partnership;
  const conversationId = partnership?.conversation_id || null;

  // Create conversation if it doesn't exist
  useEffect(() => {
    if (partnership && !partnership.conversation_id) {
      client.agency.conversations
        .createPartnership({ partnership_id: partnershipId })
        .then(() => refetchPartnership())
        .catch((error) => {
          console.error("Error creating conversation:", error);
          openAlert(
            "Error",
            `Error creating conversation: ${
              error.response?.data?.error || error.message
            }`
          );
        });
    }
  }, [partnership, partnershipId, refetchPartnership, openAlert]);

  // Fetch messages refetch function
  const { refetch: refetchMessages } = useQuery(
    ["messages", conversationId, username],
    () => API.conversations.getMessages(conversationId, { reader_id: username }),
    {
      enabled: !!conversationId,
      refetchOnWindowFocus: false,
    }
  );

  // Mutation to send message
  const sendMessageMutation = useMutation(
    ({ id, input }) => client.conversations.sendMessage(id, input),
    {
      onSuccess: () => {
        refetchMessages();
      },
      onError: (error) => {
        console.error("Error sending message:", error);
        if (error?.response?.data?.error === "forbidden") {
          openAlert(
            "Error",
            "Your message was flagged for containing prohibited content."
          );
          return;
        }
        openAlert("Error", error?.response?.data?.error || error?.message);
      },
    }
  );

  // Mutation to update deliverable
  const updateDeliverableMutation = useMutation(
    ({ id, update }) => client.deliverables.update(id, update),
    {
      onSuccess: () => {
        setSelectedDeliverable(null);
        refetchMessages();
      },
      onError: (error) => {
        console.error("Error updating deliverable:", error);
        openAlert("Error", error?.response?.data?.error || error?.message);
      },
    }
  );

  // Handlers
  const handleViewOffer = (deliverable) => setSelectedDeliverable(deliverable);
  
  const handleUpdateDeliverable = async (deliverableId, update) => {
    return updateDeliverableMutation.mutateAsync({
      id: deliverableId,
      update,
      creator_id: username,
    });
  };

  const handleSendStatusMessage = (statusMessage) => {
    if (!conversationId) return;
    sendMessageMutation.mutate({
      id: conversationId,
      input: { message: statusMessage, reader_id: username },
    });
  };

  // Set CSS variable for mobile viewport height
  useEffect(() => {
    if (isMobile) {
      const setVH = () => {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty("--vh", `${vh}px`);
      };

      setVH();
      window.addEventListener("resize", setVH);
      window.addEventListener("orientationchange", setVH);

      return () => {
        window.removeEventListener("resize", setVH);
        window.removeEventListener("orientationchange", setVH);
      };
    }
  }, [isMobile]);

  // Handle mobile chat mode toggle
  const handleMobileChatToggle = (open) => {
    setMobileChatFullscreen(open);
    if (open) {
      document.body.style.overflow = "hidden";
      setMobileActiveAccordion("chat");
    } else {
      document.body.style.overflow = "";
      setMobileActiveAccordion("details");
    }
  };

  const handleMobileAccordionChange = (panel) => {
    if (panel === "chat") {
      handleMobileChatToggle(true);
    } else if (panel === "details") {
      if (mobileActiveAccordion === "details") {
        handleMobileChatToggle(true);
      } else {
        setMobileActiveAccordion("details");
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Handle errors
  useEffect(() => {
    if (partnershipError) {
      openAlert(
        "Error",
        `Error loading partnership: ${
          partnershipError?.response?.data?.error || partnershipError.message
        }`,
        () => navigate(".."),
        () => navigate(".."),
        "OK",
        null
      );
    }
  }, [partnershipError, openAlert, navigate]);

  const renderPartnershipInfo = () => (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Partnership Header */}
      <InfoSection>
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: "primary.main",
              mb: 0.5,
              textShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            {typeof partnership?.total_sum === "number"
              ? `$${partnership.total_sum.toFixed(2)}`
              : "TBD"}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            Partnership Value
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "center",
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          {partnership?.status && (
            <Tooltip title={getStatusTooltip(partnership.status)} arrow>
              <Chip
                label={partnership.status}
                color={partnership.status === "active" ? "success" : "primary"}
                sx={{ cursor: "help", fontWeight: 600, textTransform: "capitalize" }}
              />
            </Tooltip>
          )}
        </Box>

        <Typography
          variant="h6"
          sx={{
            textAlign: "center",
            fontWeight: 600,
            color: "text.primary",
            lineHeight: 1.3,
          }}
        >
          {partnership?.name}
        </Typography>

        <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 1 }}>
          <Chip
            icon={<PersonIcon />}
            label={displayName || username}
            size="small"
            variant="outlined"
          />
          {partnership?.finish_date && (
            <Chip
              icon={<CalendarTodayIcon />}
              label={formatFinishDate(partnership.finish_date)}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </InfoSection>

      {/* Partnership Description */}
      {partnership?.description && (
        <PartnershipBriefCard elevation={0}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <DescriptionIcon color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Partnership Details
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                color: "text.secondary",
                lineHeight: 1.6,
                maxHeight: 300,
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: 4,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: alpha(theme.palette.text.primary, 0.2),
                  borderRadius: 2,
                },
              }}
            >
              {formatMessageContent(partnership.description)}
            </Typography>
          </CardContent>
        </PartnershipBriefCard>
      )}
    </Box>
  );

  if (partnershipLoading) {
    return (
      <Backdrop open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (!partnership) {
    return null;
  }

  return (
    <>
      <MainContainer>
        <PageTitle title={`Partnership: ${partnership?.name} - Agency - Blitz`} />

        {/* Desktop Layout */}
        <DesktopChatContainer>
          <DesktopSidebar elevation={2}>
            <CardContent sx={{ p: 0, height: "100%" }}>
              {renderPartnershipInfo()}
            </CardContent>
          </DesktopSidebar>

          <DesktopChatCard elevation={2}>
            <CardContent
              sx={{
                p: 2,
                pb: 1,
                borderBottom: 1,
                borderColor: "divider",
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Discuss partnership details and coordination with {displayName}
                </Typography>
                <Tooltip
                  title="Use this chat to communicate directly with the creator about partnership questions or updates"
                  arrow
                >
                  <HelpOutlineIcon
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                </Tooltip>
              </Box>
            </CardContent>

            <ChatContentWrapper>
              {conversationId ? (
                <ChatScreen
                  ref={chatRef}
                  conversationId={conversationId}
                  onDeliverableClick={handleViewOffer}
                  specificReaderId={username}
                  sx={{
                    flex: 1,
                    height: "100%",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                  showWarning={false}
                  margin={2}
                  chatSpacing={1}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1,
                    gap: 2,
                    py: 6,
                  }}
                >
                  <ChatIcon sx={{ fontSize: 64, color: "text.disabled" }} />
                  <Typography variant="h6" color="text.secondary">
                    Creating conversation...
                  </Typography>
                  <CircularProgress />
                </Box>
              )}
            </ChatContentWrapper>
          </DesktopChatCard>
        </DesktopChatContainer>

        {/* Mobile Layout */}
        <MobileChatContainer>
          {/* Mobile Header */}
          <AppBar
            position="static"
            color="default"
            elevation={0}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Toolbar>
              <IconButton edge="start" onClick={() => navigate("..")}>
                <ArrowBackIcon />
              </IconButton>
              <Box sx={{ flex: 1, ml: 1 }}>
                <Typography variant="h6" noWrap>
                  {partnership?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {displayName}
                </Typography>
              </Box>
            </Toolbar>
          </AppBar>

          {!mobileChatFullscreen ? (
            <MobileScrollableContent>
              {/* Partnership Details Accordion */}
              <MobileCampaignAccordion
                expanded={mobileActiveAccordion === "details"}
                onChange={() => handleMobileAccordionChange("details")}
                disableGutters
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: theme.palette.grey[50],
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    minHeight: 56,
                    "&.Mui-expanded": {
                      minHeight: 56,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flex: 1,
                    }}
                  >
                    <InfoIcon color="primary" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Partnership Details
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {partnership?.name} • $
                        {typeof partnership?.total_sum === "number"
                          ? partnership.total_sum.toFixed(2)
                          : "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    p: 0,
                    maxHeight: "calc(var(--vh, 1vh) * 100 - 112px)",
                    overflowY: "auto",
                    overflowX: "hidden",
                    WebkitOverflowScrolling: "touch",
                    "&::-webkit-scrollbar": {
                      width: 4,
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: alpha(theme.palette.text.primary, 0.2),
                      borderRadius: 2,
                    },
                  }}
                >
                  {renderPartnershipInfo()}
                </AccordionDetails>
              </MobileCampaignAccordion>

              {/* Partnership Chat Accordion */}
              <MobileCampaignAccordion expanded={false} disableGutters>
                <AccordionSummary
                  onClick={(e) => {
                    e.preventDefault();
                    handleMobileAccordionChange("chat");
                  }}
                  sx={{
                    backgroundColor: theme.palette.grey[50],
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    minHeight: 56,
                    "&.Mui-expanded": {
                      minHeight: 56,
                    },
                    cursor: "pointer",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ChatIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Partnership Chat
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Tap to open chat
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
              </MobileCampaignAccordion>
            </MobileScrollableContent>
          ) : (
            <MobileFullscreenChat>
              <MobileChatHeader>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ChatIcon color="primary" />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Partnership Chat
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {displayName}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={() => handleMobileChatToggle(false)}
                  edge="end"
                >
                  <CloseIcon />
                </IconButton>
              </MobileChatHeader>

              {conversationId ? (
                <ChatScreen
                  ref={chatRef}
                  conversationId={conversationId}
                  onDeliverableClick={handleViewOffer}
                  specificReaderId={username}
                  sx={{
                    flex: 1,
                    height: "100%",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                  showWarning={false}
                  margin={1}
                  chatSpacing={1}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1,
                    gap: 2,
                    py: 6,
                  }}
                >
                  <ChatIcon sx={{ fontSize: 64, color: "text.disabled" }} />
                  <Typography variant="h6" color="text.secondary">
                    Creating conversation...
                  </Typography>
                  <CircularProgress />
                </Box>
              )}
            </MobileFullscreenChat>
          )}
        </MobileChatContainer>
      </MainContainer>

      {/* Deliverable Dialog */}
      {selectedDeliverable && (
        <CreatorViewOfferDialog
          open={!!selectedDeliverable}
          onClose={() => setSelectedDeliverable(null)}
          deliverable={selectedDeliverable}
          onUpdateDeliverable={handleUpdateDeliverable}
          onSendMessage={handleSendStatusMessage}
        />
      )}

      <AlertDialog alertState={dialogState} />
    </>
  );
};

export default AgencyPartnershipPage;